import React, {useState} from 'react';
import {Tag, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import {PartnerPenaltyDetail, PartnerPenaltyStatus, PenaltyCenterMode} from '../graphql/types';
import {displayOrDash, formatIsoDateTime} from '../utils/penaltyCenterUtils';
import ParcelDetailDrawer from '../../../delivery/parcels/components/ParcelDetailDrawer';
import OrderDetailDrawer from '../../appealReview/components/OrderDetailDrawer';
import {getUserInfo} from '../../../../utils/auth';

export const STATUS_COLOR: Record<string, string> = {
    [PartnerPenaltyStatus.PENDING_SUBMIT]: 'orange',
    [PartnerPenaltyStatus.REVIEWING]: 'blue',
    [PartnerPenaltyStatus.APPROVED]: 'green',
    [PartnerPenaltyStatus.REJECTED]: 'red',
    [PartnerPenaltyStatus.TIMEOUT]: 'default',
};

export const Field: React.FC<{label: string; value: React.ReactNode; valueClassName?: string}> = ({
    label,
    value,
    valueClassName,
}) => (
    <div>
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className={valueClassName ?? 'text-sm text-gray-900'}>{value ?? '--'}</div>
    </div>
);

export const SectionTitle: React.FC<{title: React.ReactNode; hint?: React.ReactNode}> = ({title, hint}) => (
    <div className="text-base font-bold mb-3">
        {title}
        {hint && <span className="ml-2 text-xs font-normal text-gray-400">{hint}</span>}
    </div>
);

/** Tailwind 只扫字面量类名，不能拼 grid-cols-${columns}，三档都得写死在源码里 */
const GRID_COLS: Record<number, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
};

interface PenaltyBasicInfoProps {
    /**
     * 允许为空：申诉抽屉是用 Spin 罩住整块而非整块骨架屏，详情到达前会先渲染一次空态，
     * 此处逐格兜底 '--'，不能假设非空。
     */
    detail?: PartnerPenaltyDetail | null;
    /** 申诉抽屉按图4 用 4 列（抽屉已加宽到 1100px）；详情页整页用默认的 3 */
    columns?: 2 | 3 | 4;
    /** 决定点击运单号打开哪个包裹详情抽屉；申诉抽屉仅 DSP 侧使用，默认 dsp */
    mode?: PenaltyCenterMode;
}

/**
 * 运单号：有 orderId 时可点击查看包裹详情，否则退化为纯文本。
 * DSP 侧复用派送端 ParcelDetailDrawer 的只读模式（getOrderDetailForDelivery 有登录鉴权），隐藏全部编辑与操作入口；
 * 仓库侧复用总部审核的只读 OrderDetailDrawer，不暴露任何编辑入口。
 */
const TrackingNumberLink: React.FC<{
    trackingNumber?: string | null;
    orderId?: number | null;
    mode: PenaltyCenterMode;
}> = ({trackingNumber, orderId, mode}) => {
    const [open, setOpen] = useState(false);
    // 运单号或 orderId 任一缺失都无法定位包裹，只展示文本
    if (!trackingNumber || orderId == null) return <>{displayOrDash(trackingNumber)}</>;
    const close = () => setOpen(false);

    return (
        <>
            <Typography.Link onClick={() => setOpen(true)}>{trackingNumber}</Typography.Link>
            {open && mode === 'warehouse' && <OrderDetailDrawer orderId={String(orderId)} onClose={close} />}
            {open && mode === 'dsp' && (
                <ParcelDetailDrawer
                    visible={true}
                    onClose={close}
                    orderId={String(orderId)}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    embeddedReadOnly
                />
            )}
        </>
    );
};

/**
 * 罚单基本信息 9 格（warehouse 侧多一格 DSP，共 10 格）。详情页与申诉抽屉共用同一份字段定义与措辞，
 * 避免两处各写一套后续改字段漏改（列表/抽屉措辞不一致已返工过一次）。
 */
const PenaltyBasicInfo: React.FC<PenaltyBasicInfoProps> = ({detail, columns = 3, mode = 'dsp'}) => {
    const {t} = useTranslation();
    const label = (key: string) => t(`partnerControl.penaltyCenter.detail.fields.${key}`);

    return (
        <div className={`grid ${GRID_COLS[columns]} gap-x-8 gap-y-4`}>
            <Field label={label('penaltyNo')} value={displayOrDash(detail?.penaltyNo)} />
            {/* 仅 warehouse 侧查询 dspName，DSP 侧恒为空 → 这一格自动消失，不需要 mode prop */}
            {detail?.dspName && <Field label={label('dsp')} value={detail.dspName} />}
            <Field
                label={label('trackingNumber')}
                value={<TrackingNumberLink trackingNumber={detail?.trackingNumber} orderId={detail?.orderId} mode={mode} />}
            />
            <Field label={label('routeSegmentCode')} value={displayOrDash(detail?.routeSegmentCode)} />
            <Field
                label={label('status')}
                value={
                    detail == null ? (
                        '--'
                    ) : (
                        <Tag color={STATUS_COLOR[detail.status] ?? 'default'}>
                            {t(`partnerControl.penaltyCenter.status.${detail.status}`, {
                                defaultValue: detail.status,
                            })}
                        </Tag>
                    )
                }
            />
            <Field label={label('creationTime')} value={formatIsoDateTime(detail?.creationTime)} />
            <Field label={label('source')} value={displayOrDash(detail?.penaltySourceDesc)} />
            <Field
                label={label('amount')}
                value={(() => {
                    const num = detail?.amount != null ? parseFloat(detail.amount) : NaN;
                    return isNaN(num) ? '--' : `$${num.toFixed(2)}`;
                })()}
            />
            <Field label={label('driverName')} value={displayOrDash(detail?.driverName)} />
            {/* 截止申诉时间不区分状态一律标红，提醒 DSP 关注时效 */}
            <Field
                label={label('appealDeadline')}
                value={<Typography.Text type="danger">{formatIsoDateTime(detail?.appealDeadlineAt)}</Typography.Text>}
            />
        </div>
    );
};

export default PenaltyBasicInfo;
